
import { Component, Vue, Watch } from 'vue-property-decorator';
import FRTBApi from '@/api/frtb.api';
import _ from 'lodash';
import Env from '@/services/env.service';
import UtilsService from '../services/utils.service';
import GAService from '@/services/ga.service';
import { AsOf, ProfileForRequest, VenueWithEmbeddedProductInfo, Attribute, ProductWithChildren, Product, Bucket, BucketMap } from '../store/types';

@Component({
  components: {},
})
export default class CreateProfile extends Vue {

  public dataSources: VenueWithEmbeddedProductInfo[] = [];
  public attributes: any[] = [];
  public calculateLoading: boolean = false;
  public pauseCalculateTrigger: boolean = false;

  get showDevAttrCount() {
    return Env.getBool('VUE_APP_SHOW_ATTR_COUNT')
  }

/***********************************************
 * 
 *       ATTRIBUTE VALUES
 * 
 *       The values next to the attribute names.
 *       Retrieved from /distinct_attribute_values
 *         endpoint          
 * 
 ***********************************************/

    get attrValues() {
      return this.$store.state.attributeCounts.counts || {};
    }

    get attrValueQueryActive() {
      return this.$store.state.attributeCounts.queryActive || false;
    }

    public triggerAttrValuesRequest() {
      if (!this.asOfError && this.selectedDataSources.length && this.selectedProducts.length) {
        this.$store.dispatch('getAttrValues', {
          basicProfile: {
            asOf: this.asOf,
            sources: this.selectedDataSources,
            types: this.selectedProducts
          }
        });
      }
    }
  
/***********************************************
 * 
 *       OPTIONS
 * 
 *       The values to populate lists/ dropdowns,
 *       providing the options the users can 
 *       select.                
 * 
 ***********************************************/

  public get optionsData() {
    return this.$store.state.options.options || {};
  }

  public get venuesDisplayMap() {
    return this.$store.state.options.venuesDisplayMap || {};
  }

  public get attributesDisplayMap() {
    return this.$store.state.options.attributesDisplayMap || {};
  }

  public get productsById(): {[id: string] : Product} {
    return this.$store.state.options.productsById || {};
  }

  get optionsLoading() {
    return _.isEmpty(this.optionsData);
  }

  /****************************************************************************************
   *      DATA SOURCES
   *      The available data sources are dependent on which products are selected.
   *      This function looks at data-source info in our products, and creates an 
   *      array of unique data-sources (venues) for display. Each data-source item
   *      has embedded info about the products they were derived from.
   * 
   *      This function is called whenever our selectedProducts changes (see the 
   *      watcher onSelectedProductsChanged)
   ****************************************************************************************/

  public generateAvailableDataSources() {
    let sources: VenueWithEmbeddedProductInfo[] = [];
    let sourceIds: string[] = [];

    if (this.selectedProducts.length) {
      this.selectedProducts.forEach( (product: string) => {
        const venues = this.productsById[product].venues || [];
        venues.forEach( (venue: any) => {
          const index = sourceIds.indexOf(venue.id);
          if (index < 0) {
            sourceIds.push(venue.id);
            sources.push({...venue, productIds: [product]});
          } else {
            if (sources[index].productIds.indexOf(product) < 0) {
              sources[index].productIds.push(product);
              const newAttrCounts: number[] = sources[index].attribute_counts.map((a, i) => {
                return a + venue.attribute_counts[i]
              });

              sources[index].attribute_counts = newAttrCounts;
              sources[index].count = sources[index].count + venue.count;
            }
          }
        });
      });
    }

    this.dataSources = _.orderBy(sources, s => {
      return s.display ? s.display.toLowerCase() : s.id.toLowerCase();
    });
  }

  /****************************************************************************************
   *      ATTRIBUTES
   ****************************************************************************************/

  public generateAvailableAttributes() {
    if (!this.selectedDataSources.length) {
      this.$store.commit('setSelectedAttributes', []);
      this.attributes = [];
      return;
    }
    
    // Get an array of selected data source (venue) objects
    const fullVenueObjects = this.selectedDataSources.reduce((arr: VenueWithEmbeddedProductInfo[], venueId) => {
      const found:VenueWithEmbeddedProductInfo | undefined = this.dataSources.find((ds) => ds.id === venueId);
      if (found) {
        arr.push(found);
      }
      return arr;
    }, []);
    // Data Sources have a count
    const venuesCountArr = fullVenueObjects.map((fullVenue) => fullVenue.count);
    const reducer = (accumulator: number, currentValue: number) => accumulator + currentValue;
    const venuesCount = venuesCountArr.reduce(reducer);
    const minCount = Math.ceil(0.1 * venuesCount);

    const attributeCountsArrays = fullVenueObjects.map((fullVenue) => fullVenue.attribute_counts );
    const attributeCounts = attributeCountsArrays.reduce((r: number[], a) => a.map((b, i) => (r[i] || 0) + b), []);

    const attributes: Attribute[] = [];
    this.optionsData.attributes.forEach( (attr: Attribute, index: number) => {
      const count = attributeCounts[index] || 0;
      if (count >= minCount) {
        attributes.push({
          id: attr.id,
          display: this.attributesDisplayMap[attr.id] || attr.id,
          fixed_order: attr.fixed_order,
          bucketed: attr.bucketed,
          count
        });
      }
    });
    
    this.attributes = _.orderBy(attributes, 'display');

    this.updateSelectedAttributesAfterAttributeListHasBeenEdited();
  }

/***********************************************
 * 
 * 
 *              USER SELECTIONS
 * 
 * 
 ***********************************************/

  /**
   * Changes to user selectins can be triggered by loading a profile.
   * Typically, in such cases, we may want to suppress some actions that
   * selections would trigger (ie, changing a product selection would typically
   * reset selected data sources and attributes. As our profile should have 
   * its own selected data sources and attributes, we suppress the resets).
   * 
   * This flag indicates when we are loading from the profile.
   */
  get isLoadingFromProfile() {
    return this.$store.state.selections.isLoadingFromProfile || false;
  }

  @Watch('isLoadingFromProfile')
  private profileLoadingChange(value: boolean, oldValue: boolean) {
    if (!value && oldValue) {
      this.generateAvailableDataSources();
      this.generateAvailableAttributes();
      this.triggerAttrValuesRequest();
    }
  }

/****************************************************************************************
 *      PRODUCTS
 *      Products are selected by a user in the modal MultiProductModal (or when a profile 
 *      is loaded). We respond to changes with a watcher (onSelectedProductsChanged).
 ****************************************************************************************/

  public openMultiProductModal() {
    this.$store.commit('setModal', 'multiProduct');
  }

  get selectedProducts(): string[] {
    return this.$store.state.selections.selectedProducts || [];
  }

  /** 
   * Creates a string that is a list of selected products
   * Currently just used as a title (hover) attr on "n products selected" string
   */
  get selectedProductsString() {
    let str = '';
    let commaLimit = this.selectedProducts.length - 1;
    this.selectedProducts.forEach((id: string, index: number) => {
      str += this.productsById[id] ? this.productsById[id].display : id;
      if (index < commaLimit) {
        str += ', '
      }
    });

    return str;
  }

  @Watch('selectedProducts')
  private onSelectedProductsChanged(value: string[], oldValue: string[]) {
    if (!this.isLoadingFromProfile) {
      this.$store.commit('setSelectedAttributes', []);
      this.$store.commit('setSelectedDataSources', []);
      this.generateAvailableDataSources();
      this.attributes = [];
    }
  }

/****************************************************************************************
 *      DATES
 *      TODO: describe   
 ****************************************************************************************/

  public chooseDate(): void {
    this.$store.commit('setModal', 'dateSelect');
  }

  public get asOf(): AsOf {
    return this.$store.getters.getAsOf;
  }

  public get asOfDate(): Date {
    return this.$store.getters.getAsOfDate;
  }

  @Watch('asOf')
  private onAsOfChange() {
    if (!this.isLoadingFromProfile) {
      this.triggerAttrValuesRequest();
      this.calculationGateway();
    }
  }

  /**
   * TODO: describe
   */
  get asOfError(): string | undefined {
    return this.$store.state.profiles.asOfError;
  }

/****************************************************************************************
 *      DATA SOURCES
 *      Sources are selected with checkboxes on this page (or loaded from a profile).
 *      Changes triggered from this component update the store.
 *      A watcher on the store is where subsequent actions are then triggered from.
 ****************************************************************************************/

  public async toggleSelectedDataSourceItem(val: string) {
    if (this.selectedDataSources.indexOf(val) > -1) {
      const arr = this.selectedDataSources.slice();
      arr.splice(this.selectedDataSources.indexOf(val), 1);
      this.$store.commit('setSelectedDataSources', arr);
    } else {
      this.$store.commit('setSelectedDataSources', this.selectedDataSources.concat(val));
    }
  }

  public addAllDataSources() {
    if (this.selectedDataSources.length < this.dataSources.length) {
      this.$store.commit('setSelectedDataSources', this.dataSources.map((obj: any) => obj.id));
    } else {
      this.$store.commit('setSelectedDataSources', []);
    }
  }

  get selectedDataSources(): string[] {
    return this.$store.state.selections.selectedDataSources || [];
  }

  @Watch('selectedDataSources')
  private onSelectedDataSourcesChange(value: string, oldValue: string) {
    if (!this.isLoadingFromProfile) {
      const selectedAttributes = this.selectedAttributes.slice();
      this.generateAvailableAttributes();
      this.triggerAttrValuesRequest();
      // If selected attributes have changed based on generateAvailableAttributes, then
      // we don't need to trigger a calculation as our attr watcher will do that
      if (!UtilsService.compareArrays(this.selectedAttributes, selectedAttributes)) {
        this.calculationGateway();
      }
    }   
  }


/****************************************************************************************
 *      ATTRIBUTES
 *      Attributes are selected with checkboxes on this page (or loaded from a profile).
 *      Changes triggered from this component update the store.
 *      A watcher on the store is where subsequent actions are then triggered from.  
 ****************************************************************************************/

  public async selectAttributesItem(val: string) {
    if (this.selectedAttributes.indexOf(val) > -1) {
      const arr = this.selectedAttributes.slice();
      arr.splice(this.selectedAttributes.indexOf(val), 1);
      this.$store.commit('setSelectedAttributes', arr);
    } else {
      this.$store.commit('setSelectedAttributes', this.selectedAttributes.concat(val));
    }
  }

  public addAllAttributes() {
    if (this.selectedAttributes.length < this.attributes.length) {
      this.$store.commit('setSelectedAttributes', this.attributes.map((obj: any) => obj.id));
    } else {
      this.$store.commit('setSelectedAttributes', []);
    }
  }

  /**
   * When we update our list of available attribute options, we want to ensure
   * that any previously selected attributes are still in that list. This removes
   * any selected attributes that are no longer available.
   */
  public updateSelectedAttributesAfterAttributeListHasBeenEdited() {
    const original = this.selectedAttributes.slice();
    const selectedAttributes = this.selectedAttributes.filter((attrId: string) => {
      return this.attributes.find(attr => attr.id === attrId);
    });
    if (UtilsService.compareArrays(selectedAttributes, original)) {
      this.$store.commit('setSelectedAttributes', selectedAttributes);
    }
  }

  get selectedAttributes(): string[] {
    return this.$store.state.selections.selectedAttributes || [];
  }

  @Watch('selectedAttributes')
  private onSelectedAttributesChange(value: string, oldValue: string) {
    if (!this.isLoadingFromProfile) {
      this.calculationGateway();
    }
  }

/****************************************************************************************
 *      BUCKETING
 *      Bucketing is set in a modal (SelectBucketingComponent), through the loading of
 *      a profile (TODO: check), or by falling back to the default as defined in the 
 *      api response from /options.
 *      We respond to changes with a *deep* watcher (onSelectedBucketingChange).
 ****************************************************************************************/

  public selectBucketing(attributeId: string): void {
    this.$store.commit('setAttributeIdUnderEdit', attributeId);
    this.$store.commit('setModal', 'selectBucketing');
  }

  public returnSelectedBucketingString(attributeId: string): string {
    const bucket: Bucket = this.$store.getters.getBucketingForAttribute(attributeId);

    if (bucket.type === 'regulatory') {
      return bucket.id + ' (Regulatory bucketing approach)';
    } else {
      return bucket.name + ' (custom bucket)'
    }
  }

  get selectedBucketing(): BucketMap {
    return this.$store.state.buckets.selectedBucketing;
  }

  @Watch('selectedBucketing', { deep: true })
  private onSelectedBucketingChange(value: string, oldValue: string) {
    if (!this.isLoadingFromProfile) {
      this.triggerAttrValuesRequest();
      this.calculationGateway();
    }
  }


/******************************
 *
 *    CALCULATION TRIGGERS
 *
 *******************************/

  public calculationGateway() {
    if (this.selectedProducts.length && this.selectedDataSources.length && this.selectedAttributes.length) {
      this.calculate();
    } else {
      this.$store.commit('setCurrentProfileShowingOnGraph'); // TODO: check
      this.$store.commit('setSelectedGraphIndex', 0); // TODO: remove after  merge
      this.$store.commit('setResult');
    }
  }

  public async calculate() {
    this.calculateLoading = true;

    let profile: ProfileForRequest = {
      sources: this.selectedDataSources,
      types: this.selectedProducts,
      attributes: this.selectedAttributes,
      asOf: this.asOf
    };

    // Filter selected attributes by those available for
    // the selected product & data sources
    profile.attributes = profile.attributes.filter((attrId: string) => {
      return this.attributes.find( (attr: any) => {
        return attr.id === attrId;
      });
    });

    // If we have bucketing, and a bucketable attr, create attribute_params for request
    profile = UtilsService.convertBucketToAttributeParams(profile, this.optionsData);

    //this.$store.commit('setSelectedGraphIndex', 0); // TODO: check in light of Rob merge
    GAService.sendEvent(this.$gtag, 'Calculate', 'Selection', '');
    await this.$store.dispatch('calculateResults', { profile });
    this.calculateLoading = false;
  }



/********************************************
 *
 *   Adjusting displayed data sources
 *
 *********************************************/

  


/********************************************
 *
 *   Adjusting displayed attributes
 *
 *********************************************/

  

/********************************************
 *
 *   Component lifecycle methods
 *
 *********************************************/

  public async mounted() {
    try {
      this.$store.dispatch('getOptions');
    } catch (e) {
      this.$store.commit('setToastError', 'Error retrieving options. Please try signing out and in.');
    }

    this.$store.commit('setResult');
    this.$store.commit('setCurrentProfileShowingOnGraph');
  }
}
